import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { GoogleLogo, Browser, Lightning, ArrowRight } from "@phosphor-icons/react";
import { PRICES } from "@/lib/visibility";

const CHAIN = [
  {
    Icon: GoogleLogo,
    step: "Get found",
    text: "A complete Google Business Profile puts you in front of people searching nearby. Local Visibility keeps it active with fresh posts every month.",
  },
  {
    Icon: Browser,
    step: "Get chosen",
    text: "Your website is where they decide you look like the right company — and where they request a quote.",
  },
  {
    Icon: Lightning,
    step: "Respond first",
    text: "Automations text you the moment a request comes in, and can automatically text and email your customer to confirm their request was received — so they hear back before the next guy calls.",
  },
];

const RULES = [
  "Every service is billed on its own. None of them require buying a website from us.",
  "The Command Center portal comes with websites we build — not with Google Business Profile or Local Visibility on their own.",
  `The Google Business Profile setup is the same work either way: $${PRICES.gbpSetup} buys it on its own; $${PRICES.onboarding} is the onboarding rate when you start the monthly plan. Already paid $${PRICES.gbpSetup}? Onboarding is waived.`,
];

export default function HowItFits() {
  return (
    <section
      id="together"
      data-tone="light"
      data-testid="how-it-fits-section"
      className="tone-light relative py-16 sm:py-24 md:py-32 bg-white border-y border-zinc-200 scroll-mt-16 md:scroll-mt-20"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="text-[11.5px] tracking-[0.22em] uppercase text-brand-700 flex items-center gap-3">
              <span className="block w-8 h-px bg-brand-700" />
              How it fits together
            </div>
            <h2 className="mt-5 font-display font-bold text-[34px] sm:text-5xl lg:text-[56px] leading-[1.02] tracking-tight text-zinc-900">
              Found. Chosen. Answered first.
            </h2>
          </div>
          <p className="lg:col-span-5 text-zinc-600 text-[15px] sm:text-base leading-relaxed lg:pb-2">
            Each service works on its own. Together they cover the whole path from a nearby search to a booked job.
          </p>
        </div>

        <ol className="relative mt-12 sm:mt-16 grid md:grid-cols-3 gap-8 md:gap-6">
          <span aria-hidden className="hidden md:block absolute top-7 left-[17%] right-[17%] h-px bg-zinc-200" />
          {CHAIN.map((c, i) => (
            <motion.li
              key={c.step}
              data-testid={`fit-step-${i}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative flex md:flex-col gap-5 md:gap-0"
            >
              <span className="relative z-10 inline-flex items-center justify-center w-14 h-14 rounded-full bg-zinc-900 text-white flex-shrink-0 ring-8 ring-white">
                <c.Icon size={22} weight="regular" />
              </span>
              <div className="md:mt-7">
                <div className="text-[11px] tracking-[0.22em] uppercase text-brand-700">Step {i + 1}</div>
                <h3 className="mt-1.5 font-display font-bold text-[22px] sm:text-2xl tracking-tight text-zinc-900">{c.step}</h3>
                <p className="mt-3 text-[14.5px] text-zinc-600 leading-relaxed max-w-[38ch]">{c.text}</p>
              </div>
            </motion.li>
          ))}
        </ol>

        <div
          data-testid="fit-rules"
          className="mt-12 sm:mt-16 rounded-[28px] bg-[#f6f5f1] border border-zinc-200 p-6 sm:p-8 grid lg:grid-cols-[auto_1fr] gap-6 lg:gap-12 items-start"
        >
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-zinc-600">Good to know</div>
            <Link
              to="/google-business-profile"
              data-testid="fit-gbp-link"
              className="mt-3 inline-flex items-center gap-1.5 text-[13.5px] font-medium text-brand-700 hover:text-brand-500 transition-colors"
            >
              Compare the Google options
              <ArrowRight size={13} weight="bold" />
            </Link>
          </div>
          <ul className="grid sm:grid-cols-3 gap-5 sm:gap-8">
            {RULES.map((r, i) => (
              <li key={r} data-testid={`fit-rule-${i}`} className="flex items-start gap-3 text-[14px] text-zinc-700 leading-relaxed">
                <span className="mt-[9px] block w-1.5 h-1.5 rounded-full bg-brand-600 flex-shrink-0" />
                {r}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
